import { IcModule } from '@assets/icons/40x40';
import { Button, createListCollection, For, HStack, Text, VStack } from '@chakra-ui/react';
import DataSheetCard from '@components/common/layout/DataSheetCard';
import { CustomSelect } from '@components/ui/select/Select';
import { ToastStatusEnum } from '@components/ui/toaster';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole, selectActiveSite, selectActiveUserLogin } from '@redux/authent/authent.selectors';
import { selectNeutralInfo } from '@redux/situation/situation.selectors';
import { usePostMajesCommandMutation, useUpdateSensorConfigurationMutation } from '@services/c2/c2.api';
import { createSensorToast } from '@utils/sensors/sensors.utils';
import { capitalize } from 'lodash';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';
import { Dfb6ChangeConfigurationCommand, Mc2CommandEnum } from '@/types/c2/c2.types';
import { Dfb6Mode, Dfb6SensorConfiguration, OutputModeValueType } from '@/types/sensor/sensors';

type Props = {
  configuration: Dfb6SensorConfiguration;
  selectedMode: OutputModeValueType | null;
  replayMode: boolean;
};

export default function Dfb6MaintenanceActions({ configuration, selectedMode, replayMode }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [postMajesCommand] = usePostMajesCommandMutation();
  const [updateSensorConfiguration] = useUpdateSensorConfigurationMutation();
  const activeSite = useAppSelector(selectActiveSite);
  const jammerLogin = useAppSelector((state) => selectNeutralInfo(state, replayMode))?.login ?? null;
  const activeUserLogin = useAppSelector(selectActiveUserLogin);
  const hasEditRole = useAppSelector((state) => hasRole(state, RoleEnum.EDIT_NEUTRAL_SENSOR));

  const isNonActiveUserJamming = jammerLogin !== null && activeUserLogin !== jammerLogin;
  const [isLoadingChangeConfig, setIsLoadingChangeConfig] = useState<boolean>(false);

  const [newFoxes, setNewFoxes] = useState(
    new Map<OutputModeValueType, string | null>(configuration.modes.map((m) => [m.modeId, m.fox])),
  );

  const [selectedConfig, setSelectedConfig] = useState<string | undefined>(configuration.currentConfig ?? undefined);
  const [prevSelectedConfig, setPrevSelectedConfig] = useState(selectedConfig);

  if (isLoadingChangeConfig && selectedConfig === configuration.currentConfig) {
    setIsLoadingChangeConfig(false);
  }

  if (prevSelectedConfig !== configuration.currentConfig) {
    setPrevSelectedConfig(configuration.currentConfig ?? undefined);
    setSelectedConfig(configuration.currentConfig ?? undefined);
  }

  function handleChangeConfig({ modeId, name }: Dfb6Mode, newFox: string | null | undefined) {
    if (newFox && !isNonActiveUserJamming) {
      if (selectedMode === modeId && configuration.currentConfig !== newFox) {
        setIsLoadingChangeConfig(true);
        const toastParams = {
          commandValues: { name },
          command: 'sensors.commands.dfb6.changeModeConfigurationCommand',
          name: configuration.name,
        };
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.INFO,
        });
        postMajesCommand({
          appCode: configuration.appCode,
          site: activeSite?.code ?? '',
          command: {
            type: Mc2CommandEnum.ChangeConfiguration,
            sensorCode: configuration.code,
            configuration: newFox,
          } as Dfb6ChangeConfigurationCommand,
        })
          .unwrap()
          .then(() =>
            createSensorToast({
              ...toastParams,
              toast: ToastStatusEnum.SUCCESS,
            }),
          )
          .catch(() =>
            createSensorToast({
              ...toastParams,
              toast: ToastStatusEnum.ERROR,
            }),
          );
      }
      updateSensorConfiguration({
        id: configuration.id,
        site: configuration.site,
        sensor: {
          ...configuration,
          modes: configuration.modes.map((m) => (m.modeId === modeId ? { ...m, fox: newFox } : m)),
        },
      });
    }
  }

  const configurations = createListCollection({
    items: configuration.configs
      .toSorted((a, b) => a.configName.localeCompare(b.configName))
      .map(({ configName }) => ({
        label: configName,
        value: configName,
      })),
  });

  return (
    <DataSheetCard icon={IcModule} description={formatMessage({ id: 'sensors.tab.maintenance.dfb6.modesDescription' })}>
      <VStack width="100%" gap={3}>
        <For each={configuration.modes.toSorted((a, b) => b.modeId - a.modeId)}>
          {(mode) => (
            <VStack key={mode.modeId} width="100%" gap="tightL">
              <HStack width="100%" gap="standardXS">
                <Tooltip content={capitalize(mode.name.toLowerCase())} openDelay={500}>
                  <Text textStyle="sm" color="neutral.300" textOverflow="ellipsis" lineClamp={1}>
                    <FormattedMessage
                      id="sensors.tab.maintenance.dfb6.mode"
                      values={{ name: capitalize(mode.name.toLowerCase()) }}
                    />
                  </Text>
                </Tooltip>
                <Text textStyle="sm" flexShrink={0}>
                  {mode.mode.toLowerCase()}
                </Text>
              </HStack>
              {hasEditRole ? (
                <HStack width="100%" gap="tightL">
                  <CustomSelect
                    variant="darker"
                    size="sm"
                    key={mode.modeId}
                    value={newFoxes.get(mode.modeId) ?? undefined}
                    disabled={isLoadingChangeConfig || isNonActiveUserJamming || replayMode}
                    collection={configurations}
                    onChange={(value) => {
                      setNewFoxes(new Map([...newFoxes, [mode.modeId, value]]));
                    }}
                  />
                  <Button
                    disabled={newFoxes.get(mode.modeId) === mode.fox || isNonActiveUserJamming || replayMode}
                    loading={isLoadingChangeConfig}
                    size="md"
                    onClick={() => handleChangeConfig(mode, newFoxes.get(mode.modeId))}
                  >
                    <FormattedMessage id="global.validate" />
                  </Button>
                </HStack>
              ) : (
                <Text alignSelf="start">
                  {newFoxes.get(mode.modeId) ?? formatMessage({ id: 'global.noInformation' })}
                </Text>
              )}
            </VStack>
          )}
        </For>
      </VStack>
    </DataSheetCard>
  );
}
